import { StyleSheet, css } from 'aphrodite'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ActionMenu from '../components/ActionMenu'
import InvoiceConfirmationModal from '../components/report/InvoiceConfirmationModal'
import NoCompletedTagsWarningModal from '../components/report/NoCompletedTagsWarningModal'
import ViewTagModal from '../components/tag/ViewTagModal'
import { useUser } from '../contexts/UserContext'
import { statusReportService } from '../services/statusReportService'
import type { StatusReport, TagReport } from '../types/statusReport'
import type { Tag } from '../types/tag'
import { StakeholderType } from '../types/user'
import type { MultiTagReport, WorkOrderReportTag } from '../types/workorder'
import { formatDate } from '../utils/date'

const TagReportPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useUser()
  const { statusReportId } = useParams<{ statusReportId?: string }>()
  const [report, setReport] = useState<MultiTagReport | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewingTag, setViewingTag] = useState<Tag | null>(null)
  const [statusReportMeta, setStatusReportMeta] = useState<StatusReport | null>(
    null,
  )
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [completedTags, setCompletedTags] = useState<WorkOrderReportTag[]>([])

  // Determine user's stakeholder type
  const currentStakeholderType = user?.current_profile
    ?.stakeholder_type as StakeholderType
  const isSubhauler = currentStakeholderType === StakeholderType.SUB_HAULER
  const isBroker = currentStakeholderType === StakeholderType.BROKER
  const canGenerateInvoice = isSubhauler || isBroker

  // Get report data from status report ID, location state, or fetch it
  const initializeReport = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Check if we have a status report ID from URL params
      if (statusReportId) {
        const statusReport = await statusReportService.getById(
          Number.parseInt(statusReportId),
        )
        const reportData = statusReport.report_snapshot

        // Map the status report data to MultiTagReport structure
        const tags = reportData.tags || []
        const workOrders = reportData.work_orders || []

        // Calculate totals
        const calculatedTotalHours = tags.reduce(
          (sum: number, tag: TagReport) => sum + (tag.total_hours || 0),
          0,
        )
        const calculatedTotalLoads = tags.reduce(
          (sum: number, tag: TagReport) => sum + (tag.total_loads || 0),
          0,
        )
        // Create status breakdown
        const statusBreakdown = {
          new: 0,
          in_progress: 0,
          accepted: 0,
          complete: 0,
          declined: 0,
          signed: 0,
          total: tags.length,
        }

        for (const tag of tags) {
          const status = tag.status?.toLowerCase()
          if (status === 'new') statusBreakdown.new++
          else if (status === 'in_progress') statusBreakdown.in_progress++
          else if (status === 'accepted') statusBreakdown.accepted++
          else if (status === 'complete') statusBreakdown.complete++
          else if (status === 'declined') statusBreakdown.declined++
          else if (status === 'signed') statusBreakdown.signed++
        }

        // Map work orders
        const mappedWorkOrders = workOrders.map(
          (wo: Record<string, unknown>) => ({
            id: wo.id as number,
            label: (wo.label as string) || `Dispatch ${wo.id}`,
            status: (wo.status as string) || 'unknown',
            project_label:
              (wo.project as Record<string, unknown>)?.label ||
              (reportData.projects[0] as Record<string, unknown>)?.label ||
              'Unknown Project',
            subhaulers: (wo.subhaulers as unknown[]) || [],
            dispatchers: (wo.dispatchers as unknown[]) || [],
          }),
        )

        // If no work orders provided, try to extract from tags
        let extractedWorkOrders = mappedWorkOrders
        if (mappedWorkOrders.length === 0 && tags.length > 0) {
          const workOrderMap = new Map<number, Record<string, unknown>>()
          for (const tag of tags) {
            if (tag.workorder_id && !workOrderMap.has(tag.workorder_id)) {
              workOrderMap.set(tag.workorder_id, {
                id: tag.workorder_id,
                label: `Dispatch ${tag.workorder_id}`,
                status: 'unknown',
                project_label:
                  (reportData.projects[0] as Record<string, unknown>)?.label ||
                  'Unknown Project',
                subhaulers: [],
                dispatchers: [],
              })
            }
          }
          extractedWorkOrders = Array.from(workOrderMap.values())
        }

        const calculatedTotalWeight = tags.reduce(
          (sum: number, tag: TagReport) => sum + (tag.total_weight || 0),
          0,
        )

        const mappedReport: MultiTagReport = {
          report_id: statusReport.id.toString(),
          label: statusReport.label,
          generated_at: statusReport.created_date,
          selected_tag_ids:
            statusReport.tag_ids?.map(String) ||
            tags.map((tag: TagReport) => tag.id.toString()),
          total_tags: tags.length,
          total_hours: calculatedTotalHours,
          total_loads: calculatedTotalLoads,
          total_weight: calculatedTotalWeight || undefined,
          tag_status_breakdown: statusBreakdown,
          tags: tags.map((tag: TagReport) => ({
            id: tag.id,
            label: tag.label,
            title: tag.title,
            status: tag.status,
            material: tag.material,
            start_date: tag.start_date,
            destination_address: tag.destination_address,
            origin_address: tag.origin_address,
            origin_name: tag.origin_name,
            customer: tag.customer,
            notes: tag.notes,
            assigned_user: tag.assigned_user,
            assigned_preuser: tag.assigned_preuser,
            site_supervisor: tag.site_supervisor,
            total_hours: tag.total_hours,
            total_loads: tag.total_loads,
            created_date: tag.created_date,
            updated_date: tag.updated_date,
            workorder_id: tag.workorder_id,
            project_id: tag.project_id,
            truck_number: tag.truck_number,
            contractor: tag.contractor,
            broker: tag.broker,
            total_weight: tag.total_weight,
          })),
          work_orders: extractedWorkOrders,
          average_hours_per_tag:
            tags.length > 0 ? calculatedTotalHours / tags.length : 0,
          average_loads_per_tag:
            tags.length > 0 ? calculatedTotalLoads / tags.length : 0,
          earliest_tag_start: reportData.earliest_tag_start || '',
          latest_tag_completion: reportData.latest_tag_completion || '',
        }

        setReport(mappedReport)
        setStatusReportMeta(statusReport)
        return
      }

      // Check if report data was passed via navigation state (legacy flow)
      if (location.state?.report) {
        // Create a robust fallback structure to handle missing fields
        const reportData = location.state.report as Record<string, unknown>
        console.log('Raw API response:', reportData)

        // Map the actual API response to our expected structure
        const tags = (reportData.tags || []) as Record<string, unknown>[]
        const workOrders = (reportData.work_orders ||
          reportData.workorders ||
          []) as Record<string, unknown>[]

        // If no work orders provided, try to extract from tags
        let extractedWorkOrders: Record<string, unknown>[] = workOrders
        if (workOrders.length === 0 && tags.length > 0) {
          const workOrderMap = new Map<unknown, Record<string, unknown>>()
          for (const tag of tags) {
            if (tag.workorder) {
              const wo = tag.workorder as Record<string, unknown>
              const workOrderId =
                typeof tag.workorder === 'object' ? wo.id : tag.workorder
              if (!workOrderMap.has(workOrderId)) {
                workOrderMap.set(workOrderId, {
                  id: workOrderId,
                  label: wo?.label || `Dispatch ${workOrderId}`,
                  status: wo?.status || 'UNKNOWN',
                  project_label:
                    (wo?.project as Record<string, unknown>)?.label ||
                    (tag.project as Record<string, unknown>)?.label ||
                    'Unknown Project',
                  subhaulers: wo?.subhaulers || [],
                  dispatchers: wo?.dispatchers || [],
                })
              }
            }
          }
          extractedWorkOrders = Array.from(workOrderMap.values())
        }

        // Calculate totals from tags if not provided
        const calculatedTotalHours = tags.reduce(
          (sum: number, tag: Record<string, unknown>) =>
            sum + ((tag.total_hours as number) || 0),
          0,
        )
        const calculatedTotalLoads = tags.reduce(
          (sum: number, tag: Record<string, unknown>) =>
            sum + ((tag.total_loads as number) || 0),
          0,
        )
        // Calculate tag status breakdown
        const statusBreakdown = {
          new: 0,
          in_progress: 0,
          accepted: 0,
          complete: 0,
          signed: 0,
          declined: 0,
          total: tags.length,
        }

        for (const tag of tags) {
          const status = ((tag.status as string) || '').toLowerCase()
          if (status in statusBreakdown) {
            statusBreakdown[status as keyof typeof statusBreakdown]++
          }
        }

        // Map work orders to include project information
        const mappedWorkOrders = (
          (reportData.work_orders || []) as Record<string, unknown>[]
        ).map((wo: Record<string, unknown>) => ({
          id: wo.id,
          label: wo.label,
          status: wo.status,
          project_label:
            (wo.project as Record<string, unknown>)?.label ||
            ((reportData.projects as Record<string, unknown>[]) || [])?.[0]
              ?.label ||
            'Unknown Project',
          subhaulers: wo.subhaulers || [],
          dispatchers: wo.dispatchers || [],
        }))

        const safeReport: MultiTagReport = {
          report_id: reportData.report_id || reportData.id || 'unknown',
          generated_at:
            reportData.generated_at ||
            reportData.created_at ||
            new Date().toISOString(),
          selected_tag_ids:
            reportData.selected_tag_ids || reportData.tag_ids || [],
          total_tags: reportData.total_tags || tags.length || 0,
          total_hours: reportData.total_hours_worked || calculatedTotalHours,
          total_loads: reportData.total_number_of_loads || calculatedTotalLoads,
          tag_status_breakdown:
            reportData.tag_status_breakdown || statusBreakdown,
          tags: tags,
          work_orders:
            mappedWorkOrders.length > 0
              ? mappedWorkOrders
              : extractedWorkOrders,
          average_hours_per_tag:
            reportData.average_hours_per_tag ||
            (tags.length > 0 ? calculatedTotalHours / tags.length : 0),
          average_loads_per_tag:
            reportData.average_loads_per_tag ||
            (tags.length > 0 ? calculatedTotalLoads / tags.length : 0),
          earliest_tag_start: reportData.earliest_tag_start || '',
          latest_tag_completion: reportData.latest_tag_completion || '',
        }

        console.log('Safe report data:', safeReport)
        setReport(safeReport)
        setIsLoading(false)
        return
      }

      // If no report data, show error
      setError('No report data available')
      toast.error('No report data available')
    } catch (err) {
      console.error('Failed to initialize report:', err)
      setError('Failed to load report')
      toast.error('Failed to load report')
    } finally {
      setIsLoading(false)
    }
  }, [location.state, statusReportId])

  useEffect(() => {
    initializeReport()
  }, [initializeReport])

  const handleBack = () => {
    navigate(-1)
  }

  const handleViewTag = (tag: WorkOrderReportTag) => {
    const status = tag.status?.toUpperCase()
    const isCompleted = status === 'COMPLETE'

    if (isCompleted) {
      // Navigate to completed confirmation page
      navigate(`/tags/completed/${tag.id}/confirmation`)
    } else {
      // Transform tag data to include workorder and project objects for ViewTagModal
      const transformedTag = {
        ...tag,
        workorder: tag.workorder_id
          ? tag.workorder_id
          : tag.workorder
            ? tag.workorder.id
            : null,
        project: tag.project_id
          ? tag.project_id
          : tag.project
            ? tag.project.id
            : null,
      }

      // Show ViewTagModal for incomplete tags
      setViewingTag(transformedTag as Tag)
    }
  }

  const handleDownloadPdf = async () => {
    if (!statusReportId) return
    try {
      await statusReportService.downloadPdf(Number.parseInt(statusReportId))
      toast.success('PDF downloaded successfully')
    } catch (err) {
      console.error('Failed to download PDF:', err)
      toast.error('Failed to download PDF')
    }
  }

  const handleDownloadCsv = async () => {
    if (!statusReportId) return
    try {
      await statusReportService.downloadCsv(Number.parseInt(statusReportId))
      toast.success('CSV downloaded successfully')
    } catch (err) {
      console.error('Failed to download CSV:', err)
      toast.error('Failed to download CSV')
    }
  }

  const handleGenerateInvoice = () => {
    if (!report?.tags) return

    const completedTagsList = report.tags.filter(
      (tag) => tag.status?.toUpperCase() === 'COMPLETE',
    )

    if (completedTagsList.length === 0) {
      // Show warning popup for no completed tags
      setShowWarningModal(true)
    } else {
      // Show confirmation modal with completed tags
      setCompletedTags(completedTagsList)
      setShowConfirmationModal(true)
    }
  }

  const handleProceedWithEmptyInvoice = () => {
    setShowWarningModal(false)
    const invoiceRoute = isBroker
      ? '/invoices/create/broker'
      : '/invoices/create/driver'
    navigate(invoiceRoute, {
      state: { preloadedTags: [] },
    })
  }

  const handleCancelInvoice = () => {
    setShowWarningModal(false)
  }

  const handleConfirmInvoice = () => {
    setShowConfirmationModal(false)
    const invoiceRoute = isBroker
      ? '/invoices/create/broker'
      : '/invoices/create/driver'
    navigate(invoiceRoute, {
      state: { preloadedTags: completedTags },
    })
  }

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false)
    setCompletedTags([])
  }

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  // const formatCurrency = (amount: number | null) => {
  //   if (amount === null || amount === undefined) return '$0.00'
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //   }).format(amount)
  // }

  if (isLoading) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.loading)}>
          <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-5x" />
          <p>Loading tag report...</p>
        </div>
      </div>
    )
  }

  if (error || !report) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.errorContainer)}>
          <h2>Error</h2>
          <p>{error || 'Tag report not found'}</p>
          <button onClick={handleBack} className={css(styles.backButton)}>
            <i className="fa-solid fa-arrow-left" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.header)}>
        <div className={css(styles.headerLeft)}>
          <button onClick={handleBack} className={css(styles.backButton)}>
            <i className="fa-solid fa-arrow-left" />
          </button>
          <h1 className={css(styles.title)}>Status Report - {report.label}</h1>
        </div>
        <div className={css(styles.headerRight)}>
          {statusReportId && (
            <button
              onClick={handleDownloadPdf}
              className={css(styles.downloadPdfButton)}
            >
              <i className="fa-solid fa-file-pdf" />
              Download PDF
            </button>
          )}
          {statusReportId && (
            <button
              onClick={handleDownloadCsv}
              className={css(styles.downloadPdfButton)}
            >
              <i className="fa-solid fa-file-csv" />
              Download CSV
            </button>
          )}
          {canGenerateInvoice && (
            <button
              onClick={handleGenerateInvoice}
              className={css(styles.generateInvoiceButton)}
            >
              <i className="fa-solid fa-file-invoice" />
              Generate Invoice
            </button>
          )}
        </div>
      </div>

      <div className={css(styles.content)}>
        {/* Report Info (Sent Date + Sender) */}
        {statusReportMeta && (
          <div className={css(styles.section)}>
            <div className={css(styles.reportInfoGrid)}>
              <div className={css(styles.gridItem)}>
                <label className={css(styles.gridItemLabel)}>Sent Date</label>
                <span className={css(styles.gridItemSpan)}>
                  {statusReportMeta.sent_date
                    ? formatDateTime(statusReportMeta.sent_date)
                    : 'N/A'}
                </span>
              </div>
              <div className={css(styles.gridItem)}>
                <label className={css(styles.gridItemLabel)}>Sent By</label>
                <span className={css(styles.gridItemSpan)}>
                  {statusReportMeta.created_by
                    ? `${statusReportMeta.created_by.first_name || ''} ${statusReportMeta.created_by.last_name || ''}`.trim()
                    : 'N/A'}
                </span>
              </div>
              {statusReportMeta.created_by?.email && (
                <div className={css(styles.gridItem)}>
                  <label className={css(styles.gridItemLabel)}>Email</label>
                  <a
                    href={`mailto:${statusReportMeta.created_by.email}`}
                    className={css(styles.contactLink)}
                  >
                    {statusReportMeta.created_by.email}
                  </a>
                </div>
              )}
              {statusReportMeta.created_by?.phone_number && (
                <div className={css(styles.gridItem)}>
                  <label className={css(styles.gridItemLabel)}>Phone</label>
                  <a
                    href={`tel:${statusReportMeta.created_by.phone_number}`}
                    className={css(styles.contactLink)}
                  >
                    {statusReportMeta.created_by.phone_number}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Parties Involved */}
        {statusReportMeta?.report_snapshot?.parties &&
          Object.keys(statusReportMeta.report_snapshot.parties).length > 0 && (
            <div className={css(styles.section)}>
              <h2 className={css(styles.sectionTitle)}>Parties Involved</h2>
              <div className={css(styles.partiesGrid)}>
                {statusReportMeta.report_snapshot.parties.contractor && (
                  <div className={css(styles.partyCard)}>
                    <h3 className={css(styles.partyCardTitle)}>Contractor</h3>
                    <div className={css(styles.partyName)}>
                      {statusReportMeta.report_snapshot.parties.contractor
                        .first_name || ''}{' '}
                      {statusReportMeta.report_snapshot.parties.contractor
                        .last_name || ''}
                    </div>
                    {statusReportMeta.report_snapshot.parties.contractor
                      .company_name && (
                      <div className={css(styles.partyCompany)}>
                        {
                          statusReportMeta.report_snapshot.parties.contractor
                            .company_name
                        }
                      </div>
                    )}
                    {statusReportMeta.report_snapshot.parties.contractor
                      .company_address && (
                      <div className={css(styles.partyDetail)}>
                        {
                          statusReportMeta.report_snapshot.parties.contractor
                            .company_address
                        }
                      </div>
                    )}
                    {statusReportMeta.report_snapshot.parties.contractor
                      .email && (
                      <a
                        href={`mailto:${statusReportMeta.report_snapshot.parties.contractor.email}`}
                        className={css(styles.contactLink)}
                      >
                        {
                          statusReportMeta.report_snapshot.parties.contractor
                            .email
                        }
                      </a>
                    )}
                    {statusReportMeta.report_snapshot.parties.contractor
                      .phone && (
                      <div>
                        <a
                          href={`tel:${statusReportMeta.report_snapshot.parties.contractor.phone}`}
                          className={css(styles.contactLink)}
                        >
                          {
                            statusReportMeta.report_snapshot.parties.contractor
                              .phone
                          }
                        </a>
                      </div>
                    )}
                  </div>
                )}
                {statusReportMeta.report_snapshot.parties.broker && (
                  <div className={css(styles.partyCard)}>
                    <h3 className={css(styles.partyCardTitle)}>Broker</h3>
                    <div className={css(styles.partyName)}>
                      {statusReportMeta.report_snapshot.parties.broker
                        .first_name || ''}{' '}
                      {statusReportMeta.report_snapshot.parties.broker
                        .last_name || ''}
                    </div>
                    {statusReportMeta.report_snapshot.parties.broker
                      .company_name && (
                      <div className={css(styles.partyCompany)}>
                        {
                          statusReportMeta.report_snapshot.parties.broker
                            .company_name
                        }
                      </div>
                    )}
                    {statusReportMeta.report_snapshot.parties.broker
                      .company_address && (
                      <div className={css(styles.partyDetail)}>
                        {
                          statusReportMeta.report_snapshot.parties.broker
                            .company_address
                        }
                      </div>
                    )}
                    {statusReportMeta.report_snapshot.parties.broker.email && (
                      <a
                        href={`mailto:${statusReportMeta.report_snapshot.parties.broker.email}`}
                        className={css(styles.contactLink)}
                      >
                        {statusReportMeta.report_snapshot.parties.broker.email}
                      </a>
                    )}
                    {statusReportMeta.report_snapshot.parties.broker.phone && (
                      <div>
                        <a
                          href={`tel:${statusReportMeta.report_snapshot.parties.broker.phone}`}
                          className={css(styles.contactLink)}
                        >
                          {
                            statusReportMeta.report_snapshot.parties.broker
                              .phone
                          }
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {statusReportMeta.report_snapshot.parties.sub_haulers &&
                statusReportMeta.report_snapshot.parties.sub_haulers.length >
                  0 && (
                  <div
                    className={css(styles.partyCard)}
                    style={{ marginTop: 8 }}
                  >
                    <h3 className={css(styles.partyCardTitle)}>Sub Haulers</h3>
                    <div className={css(styles.partyDetail)}>
                      {statusReportMeta.report_snapshot.parties.sub_haulers
                        .map(
                          (sh: { first_name: string; last_name: string }) =>
                            `${sh.first_name} ${sh.last_name}`,
                        )
                        .join(', ')}
                    </div>
                  </div>
                )}
            </div>
          )}

        {/* Summary Metrics */}
        <div className={css(styles.section)}>
          <h2 className={css(styles.sectionTitle)}>Summary Metrics</h2>
          <div className={css(styles.metricsGrid)}>
            <div className={css(styles.metricCard)}>
              <div className={css(styles.metricValue)}>{report.total_tags}</div>
              <div className={css(styles.metricLabel)}>Total Tags</div>
            </div>
            <div className={css(styles.metricCard)}>
              <div className={css(styles.metricValue)}>
                {(report.total_hours || 0).toFixed(1)}
              </div>
              <div className={css(styles.metricLabel)}>Total Hours</div>
            </div>
            <div className={css(styles.metricCard)}>
              <div className={css(styles.metricValue)}>
                {report.total_loads || 0}
              </div>
              <div className={css(styles.metricLabel)}>Total Loads</div>
            </div>
            <div className={css(styles.metricCard)}>
              <div className={css(styles.metricValue)}>
                {report.total_weight
                  ? `${report.total_weight.toLocaleString()} lbs`
                  : '-'}
              </div>
              <div className={css(styles.metricLabel)}>Total Weight</div>
            </div>
          </div>
        </div>

        {/* Related Dispatches */}
        {report.work_orders &&
          Array.isArray(report.work_orders) &&
          report.work_orders.length > 0 && (
            <div className={css(styles.section)}>
              <h2 className={css(styles.sectionTitle)}>
                Related Dispatches ({report.work_orders.length})
              </h2>
              <div className={css(styles.workOrdersGrid)}>
                {report.work_orders.map((workOrder) => (
                  <div key={workOrder.id} className={css(styles.workOrderCard)}>
                    <div className={css(styles.workOrderHeader)}>
                      <h3 className={css(styles.workOrderTitle)}>
                        {workOrder.label}
                      </h3>
                      <span
                        className={css(
                          styles.statusBadge,
                          styles[
                            workOrder.status.toLowerCase() as keyof typeof styles
                          ],
                        )}
                      >
                        {workOrder.status}
                      </span>
                    </div>
                    <div className={css(styles.workOrderDetails)}>
                      <div className={css(styles.workOrderDetail)}>
                        <span className={css(styles.workOrderLabel)}>
                          Project:
                        </span>
                        <span className={css(styles.workOrderValue)}>
                          {workOrder.project_label}
                        </span>
                      </div>
                      {workOrder.subhaulers &&
                        Array.isArray(workOrder.subhaulers) &&
                        workOrder.subhaulers.length > 0 && (
                          <div className={css(styles.workOrderDetail)}>
                            <span className={css(styles.workOrderLabel)}>
                              Sub Hauler Corp.:
                            </span>
                            <span className={css(styles.workOrderValue)}>
                              {workOrder.subhaulers
                                .map((user) =>
                                  `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                                )
                                .join(', ')}
                            </span>
                          </div>
                        )}
                      {workOrder.dispatchers &&
                        Array.isArray(workOrder.dispatchers) &&
                        workOrder.dispatchers.length > 0 && (
                          <div className={css(styles.workOrderDetail)}>
                            <span className={css(styles.workOrderLabel)}>
                              Dispatchers:
                            </span>
                            <span className={css(styles.workOrderValue)}>
                              {workOrder.dispatchers
                                .map((user) =>
                                  `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                                )
                                .join(', ')}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Tag Status Breakdown */}
        <div className={css(styles.section)}>
          <h2 className={css(styles.sectionTitle)}>Tag Status Breakdown</h2>
          <div className={css(styles.statusBreakdown)}>
            <div className={css(styles.statusItem)}>
              <span className={css(styles.statusLabel)}>Complete</span>
              <span className={css(styles.statusCount)}>
                {report.tag_status_breakdown?.complete || 0}
              </span>
            </div>
            <div className={css(styles.statusItem)}>
              <span className={css(styles.statusLabel)}>Accepted</span>
              <span className={css(styles.statusCount)}>
                {report.tag_status_breakdown?.accepted || 0}
              </span>
            </div>
            <div className={css(styles.statusItem)}>
              <span className={css(styles.statusLabel)}>In Progress</span>
              <span className={css(styles.statusCount)}>
                {report.tag_status_breakdown?.in_progress || 0}
              </span>
            </div>
            <div className={css(styles.statusItem)}>
              <span className={css(styles.statusLabel)}>New</span>
              <span className={css(styles.statusCount)}>
                {report.tag_status_breakdown?.new || 0}
              </span>
            </div>
            <div className={css(styles.statusItem)}>
              <span className={css(styles.statusLabel)}>Declined</span>
              <span className={css(styles.statusCount)}>
                {report.tag_status_breakdown?.declined || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Tags Details */}
        <div className={css(styles.section)}>
          <h2 className={css(styles.sectionTitle)}>
            Tags Details (
            {
              (report.tags && Array.isArray(report.tags) ? report.tags : [])
                .length
            }{' '}
            tags)
          </h2>
          <div className={css(styles.tagsTableContainer)}>
            <div className={css(styles.tagsTableHeader)}>
              <div className={css(styles.tagsTableHeaderCell)}>Actions</div>
              <div className={css(styles.tagsTableHeaderCell)}>Tag</div>
              <div className={css(styles.tagsTableHeaderCell)}>Status</div>
              <div className={css(styles.tagsTableHeaderCell)}>Material</div>
              <div className={css(styles.tagsTableHeaderCell)}>Hours</div>
              <div className={css(styles.tagsTableHeaderCell)}>Loads</div>
              <div className={css(styles.tagsTableHeaderCell)}>Weight</div>
              <div className={css(styles.tagsTableHeaderCell)}>Assigned To</div>
              <div className={css(styles.tagsTableHeaderCell)}>Start Date</div>
            </div>
            <div className={css(styles.tagsTableBody)}>
              {(report.tags && Array.isArray(report.tags)
                ? report.tags
                : []
              ).map((tag) => (
                <React.Fragment key={tag.id}>
                  <div className={css(styles.tagsTableRow)}>
                    <div className={css(styles.tagsTableCell)}>
                      <ActionMenu
                        actions={[
                          {
                            label: 'View Tag',
                            icon: 'fa-solid fa-eye',
                            onClick: () => handleViewTag(tag),
                            className: css(styles.viewTagButton),
                          },
                          {
                            label: 'View Details',
                            icon: 'fa-solid fa-chevron-down',
                            onClick: () => {
                              const detailsRow = document.getElementById(
                                `tag-details-${tag.id}`,
                              )
                              if (detailsRow) {
                                detailsRow.style.display =
                                  detailsRow.style.display === 'none'
                                    ? 'block'
                                    : 'none'
                              }
                            },
                            className: css(styles.expandButton),
                          },
                        ]}
                      />
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      <div className={css(styles.tagTitleInfo)}>
                        <div className={css(styles.tagTitleText)}>
                          {tag.title}
                        </div>
                        <div className={css(styles.tagLabel)}>{tag.label}</div>
                      </div>
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      <span
                        className={css(
                          styles.statusBadge,
                          styles[
                            tag.status.toLowerCase() as keyof typeof styles
                          ],
                        )}
                      >
                        {tag.status}
                      </span>
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      {tag.material || 'N/A'}
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      {tag.total_hours || 0}
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      {tag.total_loads || 0}
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      {tag.total_weight
                        ? `${tag.total_weight.toLocaleString()} lbs`
                        : '-'}
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      {tag.assigned_user
                        ? `${tag.assigned_user.first_name || ''} ${tag.assigned_user.last_name || ''}`.trim() ||
                          'N/A'
                        : 'N/A'}
                    </div>
                    <div className={css(styles.tagsTableCell)}>
                      {tag.start_date ? formatDateTime(tag.start_date) : 'N/A'}
                    </div>
                  </div>
                  <div
                    id={`tag-details-${tag.id}`}
                    className={css(styles.tagDetailsRow)}
                    style={{ display: 'none' }}
                  >
                    <div className={css(styles.tagDetailsContent)}>
                      <div className={css(styles.tagDetailsGrid)}>
                        <div className={css(styles.tagDetailsItem)}>
                          <label className={css(styles.tagDetailsLabel)}>
                            Pick Up Address
                          </label>
                          <span className={css(styles.tagDetailsValue)}>
                            {tag.origin_address || 'N/A'}
                          </span>
                        </div>
                        <div className={css(styles.tagDetailsItem)}>
                          <label className={css(styles.tagDetailsLabel)}>
                            Drop Off Address
                          </label>
                          <span className={css(styles.tagDetailsValue)}>
                            {tag.destination_address || 'N/A'}
                          </span>
                        </div>
                        <div className={css(styles.tagDetailsItem)}>
                          <label className={css(styles.tagDetailsLabel)}>
                            Truck Number
                          </label>
                          <span className={css(styles.tagDetailsValue)}>
                            {tag.truck_number || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className={css(styles.section)}>
          <h2 className={css(styles.sectionTitle)}>Analytics</h2>
          <div className={css(styles.grid)}>
            <div className={css(styles.gridItem)}>
              <label className={css(styles.gridItemLabel)}>
                Average Hours per Tag
              </label>
              <span className={css(styles.gridItemSpan)}>
                {report.average_hours_per_tag
                  ? report.average_hours_per_tag.toFixed(2)
                  : '0.00'}
              </span>
            </div>
            <div className={css(styles.gridItem)}>
              <label className={css(styles.gridItemLabel)}>
                Average Loads per Tag
              </label>
              <span className={css(styles.gridItemSpan)}>
                {report.average_loads_per_tag
                  ? report.average_loads_per_tag.toFixed(2)
                  : '0.00'}
              </span>
            </div>
            <div className={css(styles.gridItem)}>
              <label className={css(styles.gridItemLabel)}>
                Earliest Tag Start
              </label>
              <span className={css(styles.gridItemSpan)}>
                {report.earliest_tag_start
                  ? formatDateTime(report.earliest_tag_start)
                  : 'N/A'}
              </span>
            </div>
            <div className={css(styles.gridItem)}>
              <label className={css(styles.gridItemLabel)}>
                Latest Tag Completion
              </label>
              <span className={css(styles.gridItemSpan)}>
                {report.latest_tag_completion
                  ? formatDateTime(report.latest_tag_completion)
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {viewingTag && (
        <ViewTagModal tag={viewingTag} onClose={() => setViewingTag(null)} />
      )}

      <NoCompletedTagsWarningModal
        isOpen={showWarningModal}
        onClose={handleCancelInvoice}
        onProceed={handleProceedWithEmptyInvoice}
      />

      <InvoiceConfirmationModal
        isOpen={showConfirmationModal}
        completedTags={completedTags}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmInvoice}
      />
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '20px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
    textAlign: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    backgroundColor: 'transparent',
    color: '#6B7280',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    width: '40px',
    height: '40px',
    ':hover': {
      backgroundColor: '#F9FAFB',
      color: '#4B5563',
    },
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  reportMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginLeft: 'auto',
    textAlign: 'right',
  },
  generatedAt: {
    fontSize: '12px',
    color: '#9CA3AF',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  reportInfoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  contactLink: {
    color: '#2563EB',
    textDecoration: 'none',
    fontSize: '14px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  partiesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  partyCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #E5E7EB',
  },
  partyCardTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#374151',
    margin: '0 0 8px 0',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: '8px',
  },
  partyName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '4px',
  },
  partyCompany: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '4px',
  },
  partyDetail: {
    fontSize: '13px',
    color: '#6B7280',
    marginBottom: '4px',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    margin: '0 0 16px 0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  gridItemLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  gridItemSpan: {
    fontSize: '14px',
    color: '#111827',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  metricCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    border: '1px solid #E5E7EB',
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#059669',
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  workOrdersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
  },
  workOrderCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #E5E7EB',
  },
  workOrderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  workOrderTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  workOrderDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  workOrderDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  workOrderLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: 500,
  },
  workOrderValue: {
    fontSize: '12px',
    color: '#111827',
    textAlign: 'right',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusBreakdown: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
  },
  statusItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  statusLabel: {
    fontSize: '14px',
    color: '#6B7280',
  },
  statusCount: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#111827',
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  complete: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
  },
  signed: {
    backgroundColor: '#DBEAFE',
    color: '#1E40AF',
  },
  accepted: {
    backgroundColor: '#FEF3C7',
    color: '#92400E',
  },
  in_progress: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
  new: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
  },
  declined: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
  tagsTableContainer: {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'white',
    overflowX: 'auto',
  },
  tagsTableHeader: {
    display: 'grid',
    gridTemplateColumns: '0.8fr 2fr 0.8fr 0.8fr 0.6fr 0.6fr 0.8fr 1.2fr 0.8fr',
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
    gap: '1px',
    minWidth: '800px',
  },
  tagsTableHeaderCell: {
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderRight: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
  },
  tagTitleHeader: {
    borderRight: 'none',
  },
  tagsTableBody: {
    display: 'flex',
    flexDirection: 'column',
  },
  tagsTableRow: {
    display: 'grid',
    gridTemplateColumns: '0.8fr 2fr 0.8fr 0.8fr 0.6fr 0.6fr 0.8fr 1.2fr 0.8fr',
    borderBottom: '1px solid #E5E7EB',
    gap: '1px',
    minWidth: '800px',
    ':hover': {
      backgroundColor: '#F9FAFB',
    },
  },
  tagsTableCell: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#111827',
    borderRight: '1px solid #E5E7EB',
    display: 'flex',
    alignItems: 'center',
    minHeight: '48px',
  },
  tagTitleCell: {
    borderRight: 'none',
  },
  tagTitleInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  tagTitleText: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    lineHeight: '1.2',
  },
  tagLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: '#6B7280',
    backgroundColor: '#E5E7EB',
    padding: '2px 6px',
    borderRadius: '4px',
    alignSelf: 'flex-start',
  },
  expandButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    color: '#6B7280',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#374151',
    },
  },
  viewTagButton: {
    padding: '6px 12px',
    color: '#FFFFFF',
    background: '#7C3AED',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 6,
    fontWeight: 500,
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    maxWidth: '120px',
  },
  tagDetailsRow: {
    backgroundColor: '#F9FAFB',
    borderBottom: '1px solid #E5E7EB',
  },
  tagDetailsContent: {
    padding: '16px',
  },
  tagDetailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
  tagDetailsItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  tagDetailsLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tagDetailsValue: {
    fontSize: '14px',
    color: '#111827',
  },
  noExpenses: {
    fontSize: '12px',
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  expensesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  expenseItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  expenseKey: {
    fontSize: '12px',
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  expenseValue: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#111827',
  },
  downloadPdfButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#059669',
    },
  },
  generateInvoiceButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#1955CC',
    },
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
})

export default TagReportPage
